import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit2, Loader2, Users, Inbox, MapPin, Database, Tag, FileText, Hash, GitBranch } from 'lucide-react';
import { newslettersApi, submissionsApi, NewsletterRecord, SubmissionRecord, extractLookupDisplay } from '../services/creatorApi';
import { toast } from 'sonner';
export function NewsletterDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const [newsletter, setNewsletter] = useState<NewsletterRecord | null>(null);
  const [submissions, setSubmissions] = useState<SubmissionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [activeTab, setActiveTab] = useState<'submissions' | 'subscriptions'>('submissions');
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const newsletterData = await newslettersApi.get(id);
        setNewsletter(newsletterData);
        // Load submissions for this newsletter
        if (newsletterData?.Newsletter_ID) {
          setLoadingSubmissions(true);
          try {
            const subs = await submissionsApi.findByNewsletterId(newsletterData.Newsletter_ID);
            setSubmissions(subs);
          } catch {
            // Non-blocking
          } finally {
            setLoadingSubmissions(false);
          }
        }
      } catch (err) {
        console.error('Failed to load newsletter:', err);
        toast.error('Failed to load newsletter details');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5211">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5212" />
      </div>;
  }
  if (!newsletter) {
    return <div className="text-center py-12" data-id="element-5213">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-5214">
          Newsletter not found
        </h3>
        <Link to="/newsletters" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-500" data-id="element-5215">
          <ArrowLeft className="h-4 w-4 mr-2" data-id="element-5216" />
          Back to Newsletters
        </Link>
      </div>;
  }
  // Get tag names from subform
  const tagNames = (newsletter.Newsletter_Tags || []).map(t => t.Tag_Name);
  // Get rules from subform
  const rules = newsletter.Newsletter_Category_Rules || [];
  // Split submissions by status
  const confirmedSubmissions = submissions.filter(s => s.Status === 'Confirmed');
  const pendingSubmissions = submissions.filter(s => s.Status !== 'Confirmed');
  return <div className="space-y-6" data-id="element-5217">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5218">
        <div className="flex items-center gap-4" data-id="element-5219">
          <Link to="/newsletters" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-5220">
            <ArrowLeft className="h-5 w-5" data-id="element-5221" />
          </Link>
          <div data-id="element-5222">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5223">
              {newsletter.Newsletter}
            </h1>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500" data-id="element-5224">
              <span className="flex items-center gap-1" data-id="element-5225">
                <MapPin className="h-3.5 w-3.5" data-id="element-5226" />
                {newsletter.Region || 'All Regions'}
              </span>
              <span className="flex items-center gap-1" data-id="element-5227">
                <Users className="h-3.5 w-3.5" data-id="element-5228" />
                {newsletter.Team}
              </span>
              {newsletter.Newsletter_ID && <span className="flex items-center gap-1 font-mono text-xs text-gray-400" data-id="element-5229">
                  <Hash className="h-3 w-3" data-id="element-5230" />
                  {newsletter.Newsletter_ID}
                </span>}
            </div>
          </div>
        </div>
        <Link to={`/newsletters/${id}/edit`} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-5231">
          <Edit2 className="h-4 w-4" data-id="element-5232" />
          Edit Configuration
        </Link>
      </div>

      {/* Configuration Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-5233">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-5234">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-5235">Source</h3>
          <div className="flex items-start gap-3" data-id="element-5236">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-5237">
              <FileText className="h-5 w-5 text-blue-600" data-id="element-5238" />
            </div>
            <div data-id="element-5239">
              <div className="text-sm font-medium text-gray-900" data-id="element-5240">
                {newsletter.Sign_Up_Form ? extractLookupDisplay(newsletter.Sign_Up_Form) : 'No form linked'}
              </div>
              <div className="text-xs text-gray-500 mt-1" data-id="element-5241">Sign Up Form</div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-5242">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-5243">
            CRM Destination
          </h3>
          <div className="space-y-4" data-id="element-5244">
            <div className="flex items-start gap-3" data-id="element-5245">
              <div className="p-2 bg-purple-50 rounded-lg" data-id="element-5246">
                <Database className="h-5 w-5 text-purple-600" data-id="element-5247" />
              </div>
              <div data-id="element-5248">
                {newsletter.Mailing_Lists && newsletter.Mailing_Lists.length > 0 ? <div className="space-y-1" data-id="element-5249">
                    {newsletter.Mailing_Lists.map((ml, i) => <div key={i} data-id="element-5250">
                        <div className="text-sm font-medium text-gray-900" data-id="element-5251">
                          {ml.Mailing_List_Name}
                        </div>
                        {ml.Mailing_List_ID && <div className="text-xs text-gray-400 font-mono" data-id="element-5252">
                            ID: {ml.Mailing_List_ID}
                          </div>}
                      </div>)}
                  </div> : <div className="text-sm font-medium text-gray-900" data-id="element-5253">
                    Not assigned
                  </div>}
                <div className="text-xs text-gray-500 mt-1" data-id="element-5254">
                  {newsletter.Mailing_Lists && newsletter.Mailing_Lists.length > 1 ? 'Mailing Lists' : 'Mailing List'}
                </div>
              </div>
            </div>
            {tagNames.length > 0 && <div className="flex items-start gap-3" data-id="element-5255">
                <div className="p-2 bg-green-50 rounded-lg" data-id="element-5256">
                  <Tag className="h-5 w-5 text-green-600" data-id="element-5257" />
                </div>
                <div data-id="element-5258">
                  <div className="flex flex-wrap gap-1" data-id="element-5259">
                    {tagNames.map((name, i) => <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-5260">
                        {name}
                      </span>)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-5261">Applied Tags</div>
                </div>
              </div>}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-5262">
          <h3 className="text-sm font-medium text-gray-500 mb-4" data-id="element-5263">Automation</h3>
          <div className="flex items-center justify-between" data-id="element-5264">
            <div data-id="element-5265">
              <div className="text-2xl font-semibold text-gray-900" data-id="element-5266">
                {rules.length}
              </div>
              <div className="text-sm text-gray-500" data-id="element-5267">Category Rules</div>
            </div>
            <div className="h-12 w-12 bg-orange-50 rounded-full flex items-center justify-center" data-id="element-5268">
              <GitBranch className="h-6 w-6 text-orange-600" data-id="element-5269" />
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600" data-id="element-5270">
            <span className="font-medium" data-id="element-5271">{submissions.length}</span> total
            submissions
            {confirmedSubmissions.length > 0 && <span className="text-green-600 ml-1" data-id="element-5272">
                ({confirmedSubmissions.length} confirmed)
              </span>}
          </div>
        </div>
      </div>

      {/* Category Rules Summary */}
      {rules.length > 0 && <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-5273">
          <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-5274">
            Category Rules
          </h3>
          <div className="space-y-2" data-id="element-5275">
            {rules.map((rule, i) => <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded-md" data-id="element-5276">
                <span className="text-gray-500" data-id="element-5277">If</span>
                <span className="font-medium text-gray-900" data-id="element-5278">
                  {rule.Form_Field}
                </span>
                <span className="text-gray-500" data-id="element-5279">
                  {rule.Condition.replace(/_/g, ' ')}
                </span>
                {rule.Value && <span className="font-mono text-xs bg-gray-200 px-1.5 py-0.5 rounded" data-id="element-5280">
                    {rule.Value}
                  </span>}
                <span className="text-gray-500" data-id="element-5281">→</span>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100" data-id="element-5282">
                  {rule.Category_Name}
                </span>
              </div>)}
          </div>
        </div>}

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-5283">
        <div className="border-b border-gray-200" data-id="element-5284">
          <nav className="flex -mb-px" data-id="element-5285">
            <button onClick={() => setActiveTab('submissions')} className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'submissions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5286">
              All Submissions ({submissions.length})
            </button>
            <button onClick={() => setActiveTab('subscriptions')} className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'subscriptions' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-5287">
              Confirmed ({confirmedSubmissions.length})
            </button>
          </nav>
        </div>

        <div className="p-6" data-id="element-5288">
          {loadingSubmissions ? <div className="text-center py-8" data-id="element-5289">
              <Loader2 className="mx-auto h-6 w-6 text-blue-500 animate-spin" data-id="element-5290" />
              <p className="mt-2 text-sm text-gray-500" data-id="element-5291">
                Loading submissions...
              </p>
            </div> : activeTab === 'submissions' ? submissions.length === 0 ? <div className="text-center py-12" data-id="element-5292">
                <Inbox className="mx-auto h-12 w-12 text-gray-300" data-id="element-5293" />
                <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-5294">
                  No submissions yet
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-5295">
                  Form submissions will appear here once the form is live and
                  customers start signing up.
                </p>
              </div> : <div className="overflow-x-auto" data-id="element-5296">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-5297">
                  <thead className="bg-gray-50" data-id="element-5298">
                    <tr data-id="element-5299">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5300">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5301">
                        Payload Preview
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5302">
                        ID
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-5303">
                    {submissions.map(sub => {
                let payloadPreview = '';
                try {
                  const p = JSON.parse(sub.Form_Payload);
                  payloadPreview = p.email || p.Email || JSON.stringify(p).substring(0, 60) + '...';
                } catch {
                  payloadPreview = sub.Form_Payload?.substring(0, 60) || '-';
                }
                return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-5304">
                          <td className="px-4 py-3" data-id="element-5305">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sub.Status === 'Confirmed' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-yellow-50 text-yellow-700 border border-yellow-100'}`} data-id="element-5306">
                              {sub.Status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs" data-id="element-5307">
                            {payloadPreview}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400 font-mono" data-id="element-5308">
                            {sub.ID}
                          </td>
                        </tr>;
              })}
                  </tbody>
                </table>
              </div> : confirmedSubmissions.length === 0 ? <div className="text-center py-12" data-id="element-5309">
              <Users className="mx-auto h-12 w-12 text-gray-300" data-id="element-5310" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-5311">
                No confirmed subscriptions
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-5312">
                Subscribers who confirm their double opt-in will appear here.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-5313">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-5314">
                <thead className="bg-gray-50" data-id="element-5315">
                  <tr data-id="element-5316">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5317">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5318">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-5319">
                      ID
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-5320">
                  {confirmedSubmissions.map(sub => {
                let email = '';
                try {
                  const p = JSON.parse(sub.Form_Payload);
                  email = p.email || p.Email || '-';
                } catch {
                  email = '-';
                }
                return <tr key={sub.ID} className="hover:bg-gray-50" data-id="element-5321">
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-5322">
                          {email}
                        </td>
                        <td className="px-4 py-3" data-id="element-5323">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-5324">
                            Confirmed
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-400 font-mono" data-id="element-5325">
                          {sub.ID}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </div>
      </div>
    </div>;
}